/**
 * CellVisuals.ts
 * Single source of truth for cell type colors and floor glyphs.
 * Both the 3D GridRenderer and 2D Minimap consume this registry.
 *
 * Colors follow an analogous gradient mapped to danger/progression:
 *   green → yellow-green → olive → gold → amber → orange → red-orange → red → rose
 *
 * Glyphs are Unicode symbols rendered on floor meshes via DynamicTexture.
 */

import type { CellType, CellTheme } from '../types/OctileTypes.ts'

// ─── Cell Type Visual Entry ─────────────────────────────────

export interface CellVisual {
    /** CSS hex color for minimap / UI */
    hex: string
    /** RGB 0–1 for Babylon.js Color3 */
    rgb: [number, number, number]
    /** Unicode glyph rendered on the floor */
    glyph: string
}

// ─── Analogous Gradient Palette ─────────────────────────────
// green(safe) → yellow-green(natural) → olive(constructed) → gold(treasure) →
// amber(elite) → orange(sacred) → red-orange(hazard) → red(combat) → rose(magic)

export const CELL_TYPE_VISUALS: Record<CellType, CellVisual> = {
    // ── Structural (neutral warm tones) ──
    empty:              { hex: '#4a4540', rgb: [0.29, 0.27, 0.25], glyph: '\u00B7' },  // ·
    passage:            { hex: '#4a4540', rgb: [0.29, 0.27, 0.25], glyph: '\u00B7' },  // ·
    junction:           { hex: '#5c5548', rgb: [0.36, 0.33, 0.28], glyph: '\u2726' },  // ✦
    hub:                { hex: '#6b6352', rgb: [0.42, 0.39, 0.32], glyph: '\u2731' },  // ✱
    stairs_up:          { hex: '#7a8040', rgb: [0.48, 0.50, 0.25], glyph: '\u25B2' },  // ▲
    stairs_down:        { hex: '#7a8040', rgb: [0.48, 0.50, 0.25], glyph: '\u25BC' },  // ▼
    shaft:              { hex: '#6b7038', rgb: [0.42, 0.44, 0.22], glyph: '\u2195' },  // ↕
    bridge:             { hex: '#8a8550', rgb: [0.54, 0.52, 0.31], glyph: '\u2550' },  // ═
    gate:               { hex: '#8a7840', rgb: [0.54, 0.47, 0.25], glyph: '\u2302' },  // ⌂
    secret:             { hex: '#6b6060', rgb: [0.42, 0.38, 0.38], glyph: '\u2047' },  // ⁇

    // ── Natural (yellow-green family) ──
    cave:               { hex: '#65a30d', rgb: [0.40, 0.64, 0.05], glyph: '\u25CF' },  // ●
    stalactite_cave:    { hex: '#65a30d', rgb: [0.40, 0.64, 0.05], glyph: '\u25BD' },  // ▽
    stalagmite_cave:    { hex: '#6aaa12', rgb: [0.42, 0.67, 0.07], glyph: '\u25B3' },  // △
    crystal_cave:       { hex: '#d63660', rgb: [0.84, 0.21, 0.38], glyph: '\u25C6' },  // ◆
    mushroom_grotto:    { hex: '#6d9a18', rgb: [0.43, 0.60, 0.09], glyph: '\u2618' },  // ☘
    water_pool:         { hex: '#4d8a3a', rgb: [0.30, 0.54, 0.23], glyph: '\u223F' },  // ∿
    underground_stream: { hex: '#4d8a3a', rgb: [0.30, 0.54, 0.23], glyph: '\u2248' },  // ≈
    lava_cell:          { hex: '#c43020', rgb: [0.77, 0.19, 0.13], glyph: '\u2668' },  // ♨
    ice_cave:           { hex: '#7a9a50', rgb: [0.48, 0.60, 0.31], glyph: '\u2744' },  // ❄
    collapsed:          { hex: '#5a5040', rgb: [0.35, 0.31, 0.25], glyph: '\u2592' },  // ▒

    // ── Constructed (olive-yellow family) ──
    chamber:            { hex: '#a3a322', rgb: [0.64, 0.64, 0.13], glyph: '\u25A1' },  // □
    pillared_hall:      { hex: '#a3a322', rgb: [0.64, 0.64, 0.13], glyph: '\u2384' },  // ⎄
    alcove:             { hex: '#9a9525', rgb: [0.60, 0.58, 0.15], glyph: '\u2310' },  // ⌐
    prison:             { hex: '#8a7530', rgb: [0.54, 0.46, 0.19], glyph: '\u2593' },  // ▓
    library:            { hex: '#9a8a28', rgb: [0.60, 0.54, 0.16], glyph: '\u2261' },  // ≡
    forge:              { hex: '#b58020', rgb: [0.71, 0.50, 0.13], glyph: '\u2692' },  // ⚒

    // ── Sacred (orange family) ──
    shrine:             { hex: '#ea580c', rgb: [0.92, 0.35, 0.05], glyph: '\u2724' },  // ✤
    entrance:           { hex: '#22c55e', rgb: [0.13, 0.77, 0.37], glyph: '\u2605' },  // ★
    boss_throne:        { hex: '#dc7620', rgb: [0.86, 0.46, 0.13], glyph: '\u265A' },  // ♚

    // ── Treasure (gold family) ──
    vault:              { hex: '#ca8a04', rgb: [0.79, 0.54, 0.02], glyph: '\u2622' },  // ☢ → using ◈
    cache:              { hex: '#ca8a04', rgb: [0.79, 0.54, 0.02], glyph: '\u25C8' },  // ◈
    hoard:              { hex: '#d4940a', rgb: [0.83, 0.58, 0.04], glyph: '\u2666' },  // ♦

    // ── Hazard (red-orange family) ──
    trap_floor:         { hex: '#e04830', rgb: [0.88, 0.28, 0.19], glyph: '\u26A0' },  // ⚠
    trap_walls:         { hex: '#e04830', rgb: [0.88, 0.28, 0.19], glyph: '\u2620' },  // ☠
    trap_ceiling:       { hex: '#d84028', rgb: [0.85, 0.25, 0.16], glyph: '\u2B07' },  // ⬇
    gauntlet:           { hex: '#d03820', rgb: [0.82, 0.22, 0.13], glyph: '\u2694' },  // ⚔
    puzzle:             { hex: '#c87020', rgb: [0.78, 0.44, 0.13], glyph: '\u2370' },  // ⍰
    mimic_den:          { hex: '#d84830', rgb: [0.85, 0.28, 0.19], glyph: '\u263A' },  // ☺

    // ── Combat (red family) ──
    guard_post:         { hex: '#a3a322', rgb: [0.64, 0.64, 0.13], glyph: '\u2694' },  // ⚔
    patrol_route:       { hex: '#9a8830', rgb: [0.60, 0.53, 0.19], glyph: '\u21BA' },  // ↺
    ambush:             { hex: '#b86828', rgb: [0.72, 0.41, 0.16], glyph: '\u2620' },  // ☠
    nest:               { hex: '#a07030', rgb: [0.63, 0.44, 0.19], glyph: '\u2740' },  // ❀
    arena:              { hex: '#dc7620', rgb: [0.86, 0.46, 0.13], glyph: '\u2BC0' },  // ⯀
    barricade:          { hex: '#a08030', rgb: [0.63, 0.50, 0.19], glyph: '\u2588' },  // █
}

// ─── Theme Colors ───────────────────────────────────────────

export const THEME_VISUALS: Record<CellTheme, { hex: string; rgb: [number, number, number] }> = {
    natural_cave:  { hex: '#594d38', rgb: [0.35, 0.30, 0.22] },
    ancient_stone: { hex: '#615a4d', rgb: [0.38, 0.35, 0.30] },
    crystal:       { hex: '#384873', rgb: [0.22, 0.28, 0.45] },
    organic:       { hex: '#4d2e2e', rgb: [0.30, 0.18, 0.18] },
    frozen:        { hex: '#5a6b85', rgb: [0.35, 0.42, 0.52] },
    volcanic:      { hex: '#47231a', rgb: [0.28, 0.14, 0.10] },
    overgrown:     { hex: '#2e5228', rgb: [0.18, 0.32, 0.16] },
    shadow:        { hex: '#241a33', rgb: [0.14, 0.10, 0.20] },
}

// ─── Helpers ────────────────────────────────────────────────

/** Get the hex color for a cell type, falling back to theme color */
export function getCellHexColor(type: CellType, theme: CellTheme): string {
    return CELL_TYPE_VISUALS[type]?.hex || THEME_VISUALS[theme]?.hex || '#4a4540'
}

/** Get the glyph for a cell type */
export function getCellGlyph(type: CellType): string {
    return CELL_TYPE_VISUALS[type]?.glyph || ''
}
